// frontend/src/components/current-load/CurrentLoadNotesPanel.jsx

import React, {
  useRef,
  useState,
} from "react";

import {
  useTranslation,
} from "react-i18next";

import {
  CheckCircle2,
  FileText,
  History,
  Navigation,
  Package,
  Pencil,
  UserRound,
} from "lucide-react";

function displayAuditEmail(value) {
  const email =
    String(
      value ||
      ""
    ).trim();

  return email || "—";
}

export default function CurrentLoadNotesPanel({
  load,
  notes4driver,
  notes4subcontracted,
  setNotes4driver,
  setNotes4subcontracted,
  showSubcontractedFields,
  currentTransportSession,
  currentLoadNumber,
  showAudit = true,
}) {
  const {
    t,
  } =
    useTranslation(
      "edit_load"
    );

  const [
    isEditing,
    setIsEditing,
  ] =
    useState(false);

  const snapshotRef =
    useRef(null);

  const notesDriverLength =
    String(
      notes4driver ||
      ""
    ).length;

  const notesSubcontractedLength =
    String(
      notes4subcontracted ||
      ""
    ).length;

  const beginEditing =
    () => {
      snapshotRef.current = {
        notes4driver:
          notes4driver ||
          "",

        notes4subcontracted:
          notes4subcontracted ||
          "",
      };

      setIsEditing(true);
    };

  const cancelEditing =
    () => {
      const snapshot =
        snapshotRef.current;

      if (snapshot) {
        setNotes4driver?.(
          snapshot.notes4driver
        );

        setNotes4subcontracted?.(
          snapshot.notes4subcontracted
        );
      }

      snapshotRef.current =
        null;

      setIsEditing(false);
    };

  const finishEditing =
    () => {
      snapshotRef.current =
        null;

      setIsEditing(false);
    };

  return (
    <section className="shipment-v2-panel shipment-v2-audit-panel current-load-notes-panel">
      <div className="shipment-v2-panel-header">
        <h2>
          {showAudit
            ? t(
                "workspace.notesTitle",
                {
                  defaultValue:
                    "Notas e auditoria",
                }
              )
            : t(
                "workspace.notesOnlyTitle",
                {
                  defaultValue:
                    "Notas",
                }
              )}
        </h2>

        {isEditing ? (
          <div className="edit-request-load-details-actions">
            <button
              type="button"
              className="edit-request-load-details-cancel-btn"
              onClick={cancelEditing}
            >
              {t(
                "buttons.cancel"
              )}
            </button>

            <button
              type="button"
              className="edit-request-load-details-save-btn"
              onClick={finishEditing}
            >
              <CheckCircle2
                size={15}
              />

              {t(
                "workspace.finishEditing",
                {
                  defaultValue:
                    "Concluir edição",
                }
              )}
            </button>
          </div>
        ) : (
          <button
            type="button"
            className="shipment-v2-plain-action edit-request-load-details-edit-btn"
            onClick={beginEditing}
          >
            <Pencil size={13} />

            {t(
              "workspace.editNotes",
              {
                defaultValue:
                  "Editar notas",
              }
            )}
          </button>
        )}
      </div>

      <div className="current-load-notes-overview">
        {showSubcontractedFields && (
          <div
            className={`shipment-v2-special-box current-load-note-box ${
              isEditing
                ? "is-editing"
                : ""
            }`}
          >
            <div>
              <h3>
                {t(
                  "fields.notesForSubcontractor"
                )}
              </h3>

              {isEditing ? (
                <div className="current-load-note-inline-field">
                  <textarea
                    rows={3}
                    maxLength={2000}
                    className="edit-request-load-notes-input current-load-note-inline-editor"
                    value={
                      notes4subcontracted ||
                      ""
                    }
                    placeholder={t(
                      "placeholders.notesForSubcontractor"
                    )}
                    onChange={(event) =>
                      setNotes4subcontracted?.(
                        event.target.value
                      )
                    }
                  />

                  <small className="current-load-note-counter">
                    {notesSubcontractedLength}
                    /2000
                  </small>
                </div>
              ) : (
                <p>
                  {notes4subcontracted ||
                    t(
                      "workspace.noSubcontractorNotes",
                      {
                        defaultValue:
                          "Sem notas para o subcontratado.",
                      }
                    )}
                </p>
              )}
            </div>

            <FileText
              size={15}
            />
          </div>
        )}

        <div
          className={`shipment-v2-special-box current-load-note-box ${
            isEditing
              ? "is-editing"
              : ""
          }`}
        >
          <div>
            <h3>
              {t(
                "fields.notesForDriver"
              )}
            </h3>

            {isEditing ? (
              <div className="current-load-note-inline-field">
                <textarea
                  rows={3}
                  maxLength={2000}
                  className="edit-request-load-notes-input current-load-note-inline-editor"
                  value={
                    notes4driver ||
                    ""
                  }
                  placeholder={t(
                    "placeholders.notesForDriver"
                  )}
                  onChange={(event) =>
                    setNotes4driver?.(
                      event.target.value
                    )
                  }
                />

                <small className="current-load-note-counter">
                  {notesDriverLength}
                  /2000
                </small>
              </div>
            ) : (
              <p>
                {notes4driver ||
                  t(
                    "workspace.noDriverNotes",
                    {
                      defaultValue:
                        "Sem notas para o motorista.",
                    }
                  )}
              </p>
            )}
          </div>

          <FileText
            size={15}
          />
        </div>
      </div>

      {showAudit && (
        <div className="shipment-v2-audit-list current-load-audit-list">
          <div className="shipment-v2-audit-item active">
            <div className="shipment-v2-audit-icon">
              <Navigation
                size={16}
              />
            </div>

            <div>
              <strong>
                {t(
                  "meta.transportSession"
                )}
              </strong>

              <span>
                #{currentTransportSession}
              </span>
            </div>
          </div>

          <div className="shipment-v2-audit-item info">
            <div className="shipment-v2-audit-icon">
              <Package
                size={16}
              />
            </div>

            <div>
              <strong>
                {t(
                  "meta.load"
                )}
              </strong>

              <span>
                #{currentLoadNumber}
              </span>
            </div>
          </div>

          <div className="shipment-v2-audit-item success">
            <div className="shipment-v2-audit-icon">
              <UserRound
                size={16}
              />
            </div>

            <div>
              <strong>
                {t(
                  "meta.createdBy"
                )}
              </strong>

              <span
                title={displayAuditEmail(
                  load?.createdByEmail
                )}
              >
                {displayAuditEmail(
                  load?.createdByEmail
                )}
              </span>
            </div>
          </div>

          <div className="shipment-v2-audit-item info">
            <div className="shipment-v2-audit-icon">
              <History
                size={16}
              />
            </div>

            <div>
              <strong>
                {t(
                  "meta.updatedBy"
                )}
              </strong>

              <span
                title={displayAuditEmail(
                  load?.updatedByEmail
                )}
              >
                {displayAuditEmail(
                  load?.updatedByEmail
                )}
              </span>
            </div>
          </div>
        </div>
      )}
    </section>
  );
}